// File: frontend/src/components/admin/AdminDashboard.js
//
// The admin shell: the access gate, the toolbar, the tab strip, and the wiring
// that hands each tab its data. Everything else lives in ./tabs, ./primitives
// and ./charts.
//
// ProtectedRoute guarantees a signed-in user (redirecting to /login otherwise);
// the server still rejects any account without is_admin, which is surfaced as
// the gate screen below. GET /api/admin/stats doubles as that probe — there is
// no client-side admin flag, deliberately, because a flag in the browser is not
// a permission.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Activity, BarChart3, Calendar, ArrowLeft, RefreshCw, Mail, CheckCircle,
  Shield, Search, AlertTriangle, Globe, Smartphone, Trash2, LogOut, TrendingUp,
} from 'lucide-react';
import { getApiUrl } from '../../utils/apiHelpers';
import { adminFetch, useAdminSection } from './adminApi';
import { Button, IconButton, SectionError, Sheet, Loading, EmptyState } from './primitives';
import UserDetail, { UserBadges } from './UserDetail';
import { formatRelative } from '../../utils/adminDates';
import OverviewTab from './tabs/OverviewTab';
import UsersTab from './tabs/UsersTab';
import InsightsTab from './tabs/InsightsTab';
import { ActivityTab, ChurnTab, DevicesTab, GeoTab, ErrorsTab, RangeTab } from './tabs/SimpleTabs';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'insights', label: 'Insights', icon: TrendingUp },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'churn', label: 'Churn', icon: Trash2 },
  { id: 'devices', label: 'Devices', icon: Smartphone },
  { id: 'geo', label: 'Geography', icon: Globe },
  { id: 'errors', label: 'Errors', icon: AlertTriangle },
  { id: 'custom', label: 'Custom range', icon: Calendar },
];

// Search results, and a way into any account from anywhere in the dashboard.
function UserSearchSheet({ apiUrl, onClose, initialUserId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedId, setSelectedId] = useState(initialUserId ?? null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) return;
    setSearching(true);
    setError('');
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/users/search?q=${encodeURIComponent(q)}`);
      setResults(data.users || []);
      setSearched(true);
    } catch (err) {
      setError(err.message || 'Search failed');
    }
    setSearching(false);
  }, [apiUrl]);

  return (
    <Sheet title={selectedId != null ? 'Account' : 'Find a user'} onClose={onClose}>
      {selectedId != null ? (
        <UserDetail
          apiUrl={apiUrl}
          userId={selectedId}
          onBack={() => (initialUserId != null ? onClose() : setSelectedId(null))}
          onDeleted={onClose}
        />
      ) : (
        <>
          <form
            onSubmit={(e) => { e.preventDefault(); search(query.trim()); }}
            className="flex gap-2 mb-4"
          >
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email…"
              aria-label="Search by email"
              className="flex-1 min-w-0 px-3 min-h-[44px] border border-divider rounded-xl bg-surface-input text-ink-main text-sm outline-none focus:ring-2 focus:ring-admin"
            />
            <Button variant="primary" type="submit" disabled={query.trim().length < 2 || searching}>
              {searching ? '…' : 'Search'}
            </Button>
          </form>

          <SectionError message={error} />

          {searched && results.length === 0 && !searching && <EmptyState>No accounts match</EmptyState>}

          <ul className="space-y-2">
            {results.map(u => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(u.id)}
                  className="w-full text-left p-3 border border-divider rounded-xl hover:bg-surface-input"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-ink-main break-all">{u.email}</span>
                    <UserBadges user={u} />
                  </div>
                  <div className="text-xs text-ink-light mt-1 admin-nums">
                    {u.habit_count} habits · {u.completion_count} completions ·{' '}
                    {u.last_active_at ? `seen ${formatRelative(u.last_active_at)}` : 'never seen'}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Sheet>
  );
}

export default function AdminDashboard({ user, logout }) {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => getApiUrl(), []);

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  // The server reports how many recipients it actually reached, so this holds
  // that line rather than a boolean. A send can also reach nobody — no API
  // key, no one holding the overview-email role, a provider rejection — which
  // answers 409, and that reason gets its own surface: the button used to go
  // green whatever happened, so a misconfigured deploy looked healthy here.
  const [emailSent, setEmailSent] = useState('');
  const [emailError, setEmailError] = useState('');
  // Either the search sheet, or a specific account opened from a list row.
  const [sheetUserId, setSheetUserId] = useState(undefined);

  const [secView, setSecView] = useState('important');
  const [secSeverity, setSecSeverity] = useState('all');
  const [secKind, setSecKind] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/stats`);
      setStats(data);
      setIsAuthorized(true);
      setLastRefresh(new Date());
    } catch (err) {
      if (err.status === 401) { setIsAuthorized(false); setError('Your session has expired. Please sign in again.'); }
      else if (err.status === 403) { setIsAuthorized(false); setError('This account does not have admin access.'); }
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // The signed-in account is the only credential, so check it on mount.
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Each section loads only once its tab has been opened. The old dashboard
  // fetched all seven up front, which on the admin rate limit (60 requests per
  // 15 minutes at the time) burned an eighth of the budget on a single page
  // load, most of it for tabs nobody looked at.
  // Held in state and updated from an effect rather than mutated during
  // render: a Set written to mid-render would be a side effect React is free
  // to run twice (StrictMode) or discard.
  const [visited, setVisited] = useState(() => new Set(['overview']));
  useEffect(() => {
    setVisited(prev => (prev.has(activeTab) ? prev : new Set(prev).add(activeTab)));
  }, [activeTab]);
  const opened = (id) => isAuthorized && visited.has(id);

  const insights = useAdminSection('insights', () => `${apiUrl}/api/admin/insights?days=90`, [apiUrl], { enabled: opened('insights') });
  const activity = useAdminSection('activity', () => `${apiUrl}/api/admin/activity?limit=50`, [apiUrl], { enabled: opened('activity') });
  const churn = useAdminSection('churn', () => `${apiUrl}/api/admin/churn?days=30`, [apiUrl], { enabled: opened('churn') });
  const devices = useAdminSection('devices', () => `${apiUrl}/api/admin/devices?days=30`, [apiUrl], { enabled: opened('devices') });
  const geo = useAdminSection('geo', () => `${apiUrl}/api/admin/geo?days=30`, [apiUrl], { enabled: opened('geo') });
  const errors = useAdminSection('errors', () => `${apiUrl}/api/admin/errors?limit=50`, [apiUrl], { enabled: opened('errors') });
  const security = useAdminSection(
    'security events',
    () => {
      const params = new URLSearchParams({ limit: '200' });
      if (secView === 'important') params.set('view', 'important');
      else {
        if (secSeverity !== 'all') params.set('severity', secSeverity);
        if (secKind !== 'all') params.set('kind', secKind);
      }
      return `${apiUrl}/api/admin/security-events?${params}`;
    },
    [apiUrl, secView, secSeverity, secKind],
    { enabled: opened('errors') }
  );
  const range = useAdminSection(
    'range stats',
    () => `${apiUrl}/api/admin/stats/range?start=${dateRange.start}&end=${dateRange.end}`,
    [apiUrl, dateRange.start, dateRange.end],
    { enabled: opened('custom') }
  );

  // Live feed, but only while its tab is on screen.
  useEffect(() => {
    if (!isAuthorized || activeTab !== 'activity') return;
    const id = setInterval(activity.reload, 30000);
    return () => clearInterval(id);
  }, [isAuthorized, activeTab, activity.reload]);

  const handleLogout = async () => {
    if (logout) await logout();
    navigate('/login?next=%2Fadmin');
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setEmailError('');
    setEmailSent('');
    try {
      const body = await adminFetch(`${apiUrl}/api/admin/send-daily-email`, { method: 'POST' });
      setEmailSent(body.message || 'Sent');
      setTimeout(() => setEmailSent(''), 4000);
    } catch (err) {
      // Not setError(): that banner belongs to the stats load, and clearing it
      // on the next refresh would take this message with it.
      setEmailError(err.message);
      setTimeout(() => setEmailError(''), 8000);
    }
    setSendingEmail(false);
  };

  // ── Gate ──────────────────────────────────────────────────────────────────
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[var(--color-paper-bg)] flex items-center justify-center p-4">
        <div className="bg-surface-elevated rounded-3xl shadow-xl p-6 sm:p-8 w-full max-w-md border border-divider">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-admin-fill border border-admin-border rounded-2xl flex items-center justify-center mb-4">
              <Shield className="text-admin" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-ink-main">Admin dashboard</h1>
            {loading || !error ? (
              <p className="text-ink-light mt-2 flex items-center justify-center gap-2">
                <RefreshCw size={16} className="animate-spin" />Checking admin access…
              </p>
            ) : (
              <>
                {user?.email && (
                  <p className="text-ink-light mt-2">
                    Signed in as <span className="font-medium text-ink-main break-all">{user.email}</span>
                  </p>
                )}
                <div className="text-danger text-sm bg-danger-fill border border-danger-border p-3 rounded-xl mt-4">{error}</div>
              </>
            )}
          </div>
          {!loading && error && (
            <Button variant="primary" onClick={handleLogout} className="w-full">
              <LogOut size={18} />Sign out &amp; switch account
            </Button>
          )}
          <Button variant="ghost" onClick={() => navigate('/')} className="mt-4 w-full">
            <ArrowLeft size={16} />Back to home
          </Button>
        </div>
      </div>
    );
  }

  const openUser = (id) => setSheetUserId(id);

  return (
    <div className="min-h-screen bg-[var(--color-paper-bg)]">
      {/* Toolbar. Wraps, and the action labels collapse below sm: — four
          labelled buttons plus the title came to roughly 500px of content on
          one non-wrapping row, which simply ran off a 375px screen. */}
      <header className="bg-surface-elevated border-b border-divider sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" onClick={() => navigate('/')} aria-label="Back to app" className="px-0 w-11 shrink-0">
              <ArrowLeft size={20} />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-ink-main flex items-center gap-2 truncate">
                <BarChart3 className="text-admin shrink-0" size={20} aria-hidden="true" />
                <span className="sm:hidden">Admin</span>
                <span className="hidden sm:inline">Admin dashboard</span>
              </h1>
              <p className="hidden sm:block text-xs text-ink-light">
                Updated {lastRefresh ? lastRefresh.toLocaleTimeString() : 'never'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <IconButton icon={Search} label="Search users" onClick={() => setSheetUserId(null)} />
            <IconButton
              icon={emailError ? AlertTriangle : emailSent ? CheckCircle : Mail}
              label={emailError || emailSent || 'Send daily summary'}
              onClick={handleSendEmail}
              disabled={sendingEmail}
              variant={emailError ? 'danger' : 'secondary'}
              className={emailSent ? 'text-success' : ''}
            >
              {sendingEmail ? 'Sending…' : emailError ? 'Failed' : emailSent ? 'Sent' : 'Email'}
            </IconButton>
            <IconButton icon={RefreshCw} label="Refresh stats" onClick={fetchStats} disabled={loading}>Refresh</IconButton>
            <IconButton icon={LogOut} label={user?.email ? `Sign out of ${user.email}` : 'Sign out'} onClick={handleLogout}>
              Sign out
            </IconButton>
          </div>
        </div>
      </header>

      {(emailSent || emailError) && (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-3" role="status" aria-live="polite">
          <div
            className={`rounded-xl px-4 py-3 text-sm flex items-start gap-2 border ${
              emailError
                ? 'bg-danger-fill border-danger-border text-danger'
                : 'bg-success-fill border-success-border text-success'
            }`}
          >
            {emailError ? <AlertTriangle size={16} className="shrink-0 mt-0.5" /> : <CheckCircle size={16} className="shrink-0 mt-0.5" />}
            <span className="break-words">{emailError || emailSent}</span>
          </div>
        </div>
      )}

      {/* Tab strip: scrolls horizontally with a snap and a fade at each edge,
          so it is visible that there is more to the right. */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-3">
        <div className="admin-tabstrip-wrap">
          <div className="admin-tabstrip flex gap-1.5 overflow-x-auto pb-2" role="tablist" aria-label="Admin sections">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                role="tab"
                aria-selected={activeTab === id}
                onClick={() => setActiveTab(id)}
                className={`px-3 sm:px-4 min-h-[44px] rounded-xl font-medium text-sm flex items-center gap-2 whitespace-nowrap shrink-0 ${
                  activeTab === id
                    ? 'bg-admin text-surface-card'
                    : 'text-ink-light hover:text-ink-main hover:bg-surface-input'
                }`}
              >
                <Icon size={16} aria-hidden="true" />{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 pb-10 pt-3">
        <SectionError message={error} onRetry={fetchStats} />
        {loading && !stats ? <Loading>Loading dashboard…</Loading> : (
          <>
            {activeTab === 'overview' && <OverviewTab stats={stats} onOpenUser={openUser} />}
            {activeTab === 'insights' && (
              <InsightsTab data={insights.data} error={insights.error} loading={insights.loading} onRetry={insights.reload} />
            )}
            {activeTab === 'users' && <UsersTab apiUrl={apiUrl} />}
            {activeTab === 'activity' && (
              <ActivityTab data={activity.data} error={activity.error} loading={activity.loading} onRetry={activity.reload} onOpenUser={openUser} />
            )}
            {activeTab === 'churn' && (
              <ChurnTab data={churn.data} error={churn.error} loading={churn.loading} onRetry={churn.reload} />
            )}
            {activeTab === 'devices' && (
              <DevicesTab data={devices.data} error={devices.error} loading={devices.loading} onRetry={devices.reload} />
            )}
            {activeTab === 'geo' && (
              <GeoTab data={geo.data} error={geo.error} loading={geo.loading} onRetry={geo.reload} />
            )}
            {activeTab === 'errors' && (
              <ErrorsTab
                security={security.data} errors={errors.data}
                securityError={security.error} errorsError={errors.error}
                onRetrySecurity={security.reload} onRetryErrors={errors.reload}
                view={secView} onViewChange={setSecView}
                severity={secSeverity} onSeverityChange={setSecSeverity}
                kind={secKind} onKindChange={setSecKind}
              />
            )}
            {activeTab === 'custom' && (
              <RangeTab
                data={range.data} error={range.error} loading={range.loading} onRetry={range.reload}
                range={dateRange}
                onRangeChange={(start, end) => setDateRange({ start, end })}
              />
            )}
          </>
        )}
      </main>

      {sheetUserId !== undefined && (
        <UserSearchSheet
          apiUrl={apiUrl}
          initialUserId={sheetUserId ?? undefined}
          onClose={() => setSheetUserId(undefined)}
        />
      )}
    </div>
  );
}
